import { Link } from '@tanstack/react-router';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { ChevronRight } from 'lucide-react';

import { Badge } from '@apps/web/components/ui/badge';
import { cn } from '@apps/web/lib/utils';
import {
    type AgentTraceChildRunRead,
    type AgentTraceDetail,
    type AgentTraceRelatedRunRead,
} from '@libraries/typescript/api_client';

export type AgentTraceRunRelationship = NonNullable<
    AgentTraceDetail['run_relationships']
>[number];

export function formatRunTypeLabel(runType: string): string {
    return runType
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

export function formatRunRelationshipLabel(
    relationship: AgentTraceRunRelationship,
): string {
    switch (relationship.relationship_type) {
        case 'spawned_slack_outreach_thread':
            return relationship.direction === 'outgoing'
                ? 'Spawned Slack outreach thread'
                : 'Source Slack thread';
        case 'spawned_general_agent_probe':
            return relationship.direction === 'outgoing'
                ? 'Spawned General Agent probe'
                : 'Source General Agent probe';
        case 'spawned_staff_imessage_topic_run':
            return relationship.direction === 'outgoing'
                ? 'Spawned iMessage subagent run'
                : 'Source iMessage chat';
        default:
            return formatRunTypeLabel(relationship.relationship_type);
    }
}

export function RelatedRunLink({
    label,
    run,
    variant = 'card',
}: {
    readonly label?: string;
    readonly run: AgentTraceRelatedRunRead | AgentTraceChildRunRead;
    readonly variant?: 'card' | 'inline';
}) {
    const resolvedLabel =
        label ?? run.title ?? formatRunTypeLabel(run.run_type);
    const childProgress =
        'activity' in run
            ? {
                  activity: run.activity,
                  lastEpisodeOutcome: run.last_episode_outcome,
                  lastActivityAt: run.last_activity_at,
              }
            : null;
    const content =
        variant === 'inline' ? (
            resolvedLabel
        ) : (
            <div className="flex min-w-0 items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm">
                <div className="min-w-0">
                    <div className="truncate font-medium">{resolvedLabel}</div>
                    <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-2 text-xs">
                        <span>{formatRunTypeLabel(run.run_type)}</span>
                        <span>{run.status.replace('_', ' ')}</span>
                        {childProgress ? (
                            <span
                                className={cn(
                                    'font-medium',
                                    childProgress.activity === 'working'
                                        ? 'text-emerald-600 dark:text-emerald-400'
                                        : 'text-muted-foreground',
                                )}
                            >
                                {childProgress.activity === 'working'
                                    ? 'working now'
                                    : 'idle'}
                            </span>
                        ) : null}
                        {childProgress?.lastEpisodeOutcome ? (
                            <span>
                                last episode{' '}
                                {childProgress.lastEpisodeOutcome.replace(
                                    '_',
                                    ' ',
                                )}
                            </span>
                        ) : null}
                        {childProgress ? (
                            <span>
                                activity{' '}
                                {formatDistanceToNowStrict(
                                    new Date(childProgress.lastActivityAt),
                                    { addSuffix: true },
                                )}
                            </span>
                        ) : null}
                        <span>
                            {format(new Date(run.created_at), 'MMM d, h:mm a')}
                        </span>
                        <span className="font-mono">
                            {run.run_id.slice(0, 8)}
                        </span>
                    </div>
                </div>
                <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
            </div>
        );
    const className = cn(
        variant === 'inline'
            ? 'text-primary underline'
            : 'hover:bg-muted/50 block rounded-md',
    );

    if (run.conversation_id) {
        return (
            <Link
                to="/admin/agent-traces/$conversationId"
                params={{ conversationId: run.conversation_id }}
                className={className}
            >
                {content}
            </Link>
        );
    }

    return (
        <Link
            to="/admin/agent-traces/run/$runId"
            params={{ runId: run.run_id }}
            className={className}
        >
            {content}
        </Link>
    );
}

function RunRelationshipList({
    relationships,
}: {
    readonly relationships: Array<AgentTraceRunRelationship>;
}) {
    return (
        <div className="grid gap-3">
            {relationships.map((relationship) => (
                <div key={relationship.id} className="grid gap-1.5">
                    <div className="text-muted-foreground text-xs font-medium">
                        {formatRunRelationshipLabel(relationship)}
                    </div>
                    <RelatedRunLink run={relationship.related_run} />
                </div>
            ))}
        </div>
    );
}

export function SubagentRunsPanel({
    childRuns,
    parentRun,
    runRelationships = [],
    runType,
}: {
    readonly childRuns: Array<AgentTraceRelatedRunRead>;
    readonly parentRun: AgentTraceRelatedRunRead | null | undefined;
    readonly runRelationships?: Array<AgentTraceRunRelationship>;
    readonly runType: string | null | undefined;
}) {
    const outgoingRelationships = runRelationships.filter(
        (relationship) => relationship.direction === 'outgoing',
    );
    const incomingRelationships = runRelationships.filter(
        (relationship) => relationship.direction === 'incoming',
    );
    if (!parentRun && childRuns.length === 0 && runRelationships.length === 0) {
        return (
            <div className="bg-card flex items-center justify-center rounded-xl border py-8">
                <div className="text-muted-foreground text-sm">
                    No related runs.
                </div>
            </div>
        );
    }

    return (
        <div className="bg-card rounded-xl border p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-sm font-semibold">Related runs</h2>
                {runType ? (
                    <Badge variant="secondary">
                        {formatRunTypeLabel(runType)}
                    </Badge>
                ) : null}
            </div>
            <div className="grid gap-4">
                {parentRun ? (
                    <section>
                        <h3 className="text-muted-foreground mb-2 text-xs font-medium">
                            Parent
                        </h3>
                        <RelatedRunLink run={parentRun} />
                    </section>
                ) : null}
                {childRuns.length > 0 ? (
                    <section>
                        <h3 className="text-muted-foreground mb-2 text-xs font-medium">
                            Children
                        </h3>
                        <div className="grid gap-2">
                            {childRuns.map((childRun) => (
                                <RelatedRunLink
                                    key={childRun.run_id}
                                    run={childRun}
                                />
                            ))}
                        </div>
                    </section>
                ) : null}
                {outgoingRelationships.length > 0 ? (
                    <section>
                        <h3 className="text-muted-foreground mb-2 text-xs font-medium">
                            Created from this run
                        </h3>
                        <RunRelationshipList
                            relationships={outgoingRelationships}
                        />
                    </section>
                ) : null}
                {incomingRelationships.length > 0 ? (
                    <section>
                        <h3 className="text-muted-foreground mb-2 text-xs font-medium">
                            Sources for this run
                        </h3>
                        <RunRelationshipList
                            relationships={incomingRelationships}
                        />
                    </section>
                ) : null}
            </div>
        </div>
    );
}
